import type React from "react";
import { DragDropProvider, type DragDropEventHandlers } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";

import { type DocumentSummary } from "~/api/types";
import { DocumentCard, DocumentLines } from "./DocumentCard";

export type OnDragEnd = NonNullable<DragDropEventHandlers["onDragEnd"]>;

interface TierRowProps {
  documents: DocumentSummary[];
  // Override for the `documents` prop passed to DocumentLines, which is a
  // dep in its measure useEffect. Grid mode passes the server-state array so
  // lines don't re-measure on every optimistic drag update.
  linesDocuments?: DocumentSummary[];
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  hoveredId?: number;
  setHoveredId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSelectedId: (id: number) => void;
  emptySlots?: number;
  sortable?: { type: string; onDragEnd: OnDragEnd };
}

export const TierRow = ({
  documents,
  linesDocuments = documents,
  documentRefsMap,
  containerRef,
  hoveredId,
  setHoveredId,
  setSelectedId,
  emptySlots = 0,
  sortable,
}: TierRowProps) => {
  const cards = sortable ? (
    <DragDropProvider onDragEnd={sortable.onDragEnd}>
      {documents.map((document, index) => (
        <SortableDocumentCard
          key={document.id}
          index={index}
          sortableType={sortable.type}
          document={document}
          documentRefsMap={documentRefsMap}
          hoveredId={hoveredId}
          setHoveredId={setHoveredId}
          setSelectedId={setSelectedId}
        />
      ))}
    </DragDropProvider>
  ) : (
    documents.map((document) => (
      <DocumentCard
        key={document.id}
        document={document}
        documentRefsMap={documentRefsMap}
        hoveredId={hoveredId}
        setHoveredId={setHoveredId}
        setSelectedId={setSelectedId}
      />
    ))
  );

  return (
    <div className="odd:bg-accent/70 even:bg-background flex h-44 items-center gap-2 px-4">
      {documents.map((document) => (
        <DocumentLines
          key={`lines-${document.id}`}
          document={document}
          documentRefsMap={documentRefsMap}
          containerRef={containerRef}
          hoveredId={hoveredId}
          documents={linesDocuments}
        />
      ))}
      {cards}
      {Array.from({ length: emptySlots }, (_, i) => (
        <div key={`empty-${i}`} className="size-37.5 shrink-0" />
      ))}
    </div>
  );
};

interface SortableDocumentCardProps {
  index: number;
  sortableType: string;
  document: DocumentSummary;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  hoveredId?: number;
  setHoveredId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSelectedId: (id: number) => void;
}

const SortableDocumentCard = ({
  index,
  sortableType,
  document,
  documentRefsMap,
  hoveredId,
  setHoveredId,
  setSelectedId,
}: SortableDocumentCardProps) => {
  const { ref, handleRef, isDragging } = useSortable({
    id: `${document.id}`,
    index,
    type: sortableType,
    accept: sortableType,
  });

  return (
    <div ref={ref}>
      <DocumentCard
        document={document}
        documentRefsMap={documentRefsMap}
        hoveredId={hoveredId}
        setHoveredId={setHoveredId}
        setSelectedId={setSelectedId}
        isDragging={isDragging}
        handleRef={handleRef}
      />
    </div>
  );
};
